/**
 * TodayAgendaWidget — premium "next clients today" panel for the Dashboard.
 *
 * Why this widget exists:
 *   Operators told us the dashboard was a passive read-out — they had to
 *   navigate to /agendamentos to see who's coming next. This card surfaces
 *   the next up-to-3 appointments TODAY at the top of the home page, with
 *   the same one-click detail flow the calendar exposes.
 *
 * Click target: deep-link `/app/agendamentos?open=<id>` so the existing
 * AppointmentDetailsModal handles the heavy lifting — no duplicated UI.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarBlank, Clock, CarSimple, Sparkle, ArrowRight } from "@phosphor-icons/react";

const STATUS_LABEL = {
  proposed: "Proposto",
  confirmed: "Confirmado",
  checked_in: "Chegou",
  in_service: "Em atendimento",
  attended: "Finalizado",
  no_show: "Não compareceu",
  paid: "Pago",
};

const STATUS_DOT = {
  proposed: "bg-blue-500",
  confirmed: "bg-emerald-500",
  checked_in: "bg-amber-500",
  in_service: "bg-indigo-500",
  attended: "bg-slate-700",
  no_show: "bg-rose-500",
  paid: "bg-emerald-600",
};

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit", minute: "2-digit",
  });
}

function relativeBucket(iso) {
  const now = new Date();
  const d = new Date(iso);
  const diffMin = Math.round((d - now) / 60000);
  if (diffMin < -5) return { label: "passou", tone: "text-slate-400" };
  if (diffMin <= 15) return { label: "agora", tone: "text-rose-600" };
  if (diffMin <= 60) return { label: `em ${diffMin}min`, tone: "text-amber-700" };
  if (diffMin <= 240) return { label: `em ${Math.round(diffMin / 60)}h`, tone: "text-slate-600" };
  const h = d.getHours();
  return { label: h < 12 ? "manhã" : h < 18 ? "tarde" : "noite", tone: "text-slate-500" };
}

export default function TodayAgendaWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: r } = await api.get("/dashboard/today_agenda?limit=3");
        if (!cancelled) setData(r);
      } catch {
        // Silent — widget just stays minimal if it fails.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Refresh every 60s so an operator who leaves the dashboard open during
    // a busy day sees status changes propagate without a manual reload.
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 animate-pulse" data-testid="today-agenda-loading">
        <div className="h-3 w-32 bg-slate-200 rounded" />
        <div className="mt-4 space-y-3">
          <div className="h-12 bg-slate-100 rounded-md" />
          <div className="h-12 bg-slate-100 rounded-md" />
        </div>
      </div>
    );
  }

  const items = data?.items || [];
  const totalToday = data?.total_today ?? 0;

  // No appointments → show a calm "tudo livre" state instead of hiding.
  if (items.length === 0) {
    return (
      <section
        data-testid="today-agenda-empty"
        className="relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-white via-white to-slate-50 p-5"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
            <CalendarBlank size={15} weight="regular" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Agenda de hoje</div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 tracking-tight mt-0.5">
              Nenhum cliente agendado para hoje.
            </h3>
            <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">
              Quando o AgentUS — ou você — fechar um agendamento, ele aparece aqui em destaque.
            </p>
            <Link
              to="/app/agendamentos"
              data-testid="today-agenda-empty-cta"
              className="inline-flex items-center gap-1 mt-3 text-[12px] font-medium text-slate-700 hover:text-slate-900"
            >
              Abrir agenda <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="today-agenda-widget"
      className="relative overflow-hidden rounded-xl border border-slate-200 bg-white"
    >
      {/* Subtle premium accent — a thin gradient strip on the left edge */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-slate-900 via-slate-700 to-slate-900" />

      <header className="px-5 md:px-6 pt-5 pb-3 flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" className="text-slate-700" />
            Agenda de hoje
          </div>
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-slate-900 tracking-tight mt-1 leading-tight">
            {items.length === 1 && totalToday === 1 ? (
              <>1 cliente confirmado hoje.</>
            ) : (
              <>
                Próximos <span className="tabular-nums">{items.length}</span> de{" "}
                <span className="tabular-nums">{totalToday}</span>{" "}
                {totalToday === 1 ? "agendamento" : "agendamentos"} de hoje.
              </>
            )}
          </h3>
        </div>
        <Link
          to="/app/agendamentos"
          data-testid="today-agenda-see-all"
          className="hidden md:inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-slate-900 transition-colors shrink-0"
        >
          ver agenda completa <ArrowRight size={11} weight="bold" />
        </Link>
      </header>

      <ul className="px-2 md:px-3 pb-3">
        {items.map((a) => {
          const r = relativeBucket(a.scheduled_at);
          return (
            <li key={a.id}>
              <button
                onClick={() => navigate(`/app/agendamentos?open=${a.id}`)}
                data-testid={`today-agenda-item-${a.id}`}
                className="group w-full text-left px-3 md:px-4 py-3 rounded-lg flex items-center gap-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex flex-col items-center justify-center w-14 shrink-0">
                  <div className="font-heading text-lg font-semibold text-slate-900 tabular-nums leading-none">
                    {fmtTime(a.scheduled_at)}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider font-medium mt-1 ${r.tone}`}>
                    {r.label}
                  </div>
                </div>

                <div className="w-px h-10 bg-slate-200 shrink-0" />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[a.status] || "bg-slate-400"}`} />
                    <span className="font-semibold text-slate-900 text-[15px] truncate">
                      {a.customer_name}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[12.5px] text-slate-600 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="truncate">{a.service_name}</span>
                    {a.vehicle_label && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1 truncate">
                          <CarSimple size={11} weight="regular" className="text-slate-400" />
                          {a.vehicle_label}
                        </span>
                      </>
                    )}
                    <span className="text-slate-300">·</span>
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      <Clock size={11} weight="regular" />
                      {a.duration_min}min
                    </span>
                  </div>
                </div>

                <span className="text-[10px] uppercase tracking-wider text-slate-500 group-hover:text-slate-900 transition-colors hidden md:inline-flex items-center gap-1 shrink-0">
                  {STATUS_LABEL[a.status] || a.status}
                  <ArrowRight size={10} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
